import { parentsUntil, getPreviousFocusableElement, getNextFocusableElement, getAllFocusableElementsWithinElement } from "../lib/utils";

let isOpen = false;

/**
 * Opens the meganav.
 */
const open = () => {
	if (isOpen) {
		return;
	}

	document.body.dataset.meganavOpen = 'true';

	isOpen = true;
};

/**
 * Closes the meganav.
 */
const close = () => {
	if (!isOpen) {
		return;
	}

	document.body.dataset.meganavOpen = 'false';

	isOpen = false;
};

const tabsList = [];

const onKeyDownOnTab = (e) => {
	switch (e.key) {
		case 'Tab':
			if (e.shiftKey) {
				focusPreviousElementAfterMegaNav();
			} else {
				focusNextElementAfterMegaNav();
			}
			stopEventContinuing(e);
			break;
		case 'ArrowLeft':
			selectPreviousTab(e.currentTarget);
			stopEventContinuing(e);
			break;
		case 'ArrowRight':
			selectNextTab(e.currentTarget);
			stopEventContinuing(e);
			break;
		case 'ArrowDown':
		case 'Enter':
			tabIntoChildren(e.currentTarget, true);
			stopEventContinuing(e);
			break;
		default:
			break;
	}
}

const setTabListLinkActive = (elem) => {
	// Clear all active links first
	document.querySelectorAll('.meganav__tablist__link').forEach(link => {
		link.classList.remove('is-active');
	});

	elem.classList.add('is-active');
}

const onKeyDownOnTabListLink = (e) => {
	const activeTabList = parentsUntil(e.currentTarget, '.meganav__tablist');
	const activeTabPanel = parentsUntil(e.currentTarget, '.meganav__panel');

	switch (e.key) {
		case 'Enter':
		case 'ArrowRight':
			showInnerTabContent(e.currentTarget);
			setTabListLinkActive(e.currentTarget);
			stopEventContinuing(e);
			break;
		case 'ArrowUp':
			const prevElem = getPreviousFocusableElement(e.currentTarget, activeTabList[0]);
			if (prevElem) {
				prevElem?.focus();
			}

			stopEventContinuing(e);
			break;
		case 'ArrowDown':
			// Select next tab link in list
			const nextElem = getNextFocusableElement(e.currentTarget, activeTabList[0]);
			if (nextElem) {
				nextElem?.focus();
			}
			stopEventContinuing(e);
			break;
		case 'Tab':
			// skip past the mega nav
			closeAllTabs();

			if (e.shiftKey) {
				focusPreviousElementAfterMegaNav();
			} else {
				focusNextElementAfterMegaNav();
			}
			stopEventContinuing(e);
			break;
		case 'ArrowLeft':
			if (!activeTabPanel || activeTabPanel.length == 0) { console.warn("No parent panel found"); return; }
			const relatedTabListId = activeTabPanel[0].getAttribute('aria-labelledby');
			const relatedTab = document.querySelector(`#${relatedTabListId}`);
			relatedTab?.focus();

			// Clear all active links first
			document.querySelectorAll('.meganav__tablist__link.is-active').forEach(link => {
				link.classList.remove('is-active');
			});

			if (areAllElementsClosed()) {
				close();
			}

			stopEventContinuing(e);
			break;
		default:
			break;
	}
}

const onKeyDownOnInnerTabLink = (e) => {
	const parentScope = parentsUntil(e.currentTarget, '.meganav__tab.innerTab__content');
	const parentPanel = parentsUntil(e.currentTarget, '.meganav__panel');
	switch (e.key) {
		case 'ArrowLeft':
			const relatedTabListId = parentScope[0].getAttribute('id');
			// parentScope[0].classList.remove('is-active');
			// const relatedTabList = document.querySelector(`[aria-controls=${relatedTabListId}]`);
			// relatedTabList?.classList.remove('is-active');

			const relatedLink = document.querySelector(`.meganav__tablist__link[aria-controls=${relatedTabListId}]`);
			relatedLink?.focus();

			// tabContentPanelDisplayDefaultContent(parentPanel[0], true);
			break;
		case 'ArrowUp':
			const prevElem = getPreviousFocusableElement(e.currentTarget, parentScope[0]);
			if (prevElem) {
				prevElem?.focus();
			}

			stopEventContinuing(e);
			break;
		case 'ArrowDown':
			// Select next tab link in list
			const nextElem = getNextFocusableElement(e.currentTarget, parentScope[0]);
			if (nextElem) {
				nextElem?.focus();
			}
			stopEventContinuing(e);
			break;
		case 'Tab':
			// skip past the mega nav
			closeAllTabs();

			if (e.shiftKey) {
				focusPreviousElementAfterMegaNav();
			} else {
				focusNextElementAfterMegaNav();
			}
			stopEventContinuing(e);
			break;
		default:
			break;
	}
}

const stopEventContinuing = (e) => {
	e.preventDefault();
	e.stopPropagation();
}

const focusNextElementAfterMegaNav = () => {
	const lastLevelOneItem = document.querySelector('.meganav__menu .nav__item:last-child .meganav__menu__link ');
	const nextElem = getNextFocusableElement(lastLevelOneItem);
	nextElem?.focus();
}

const focusPreviousElementAfterMegaNav = () => {
	const firstLevelOneItem = document.querySelector('.meganav__menu .nav__item:first-child .meganav__menu__link ');
	const previousElem = getPreviousFocusableElement(firstLevelOneItem);
	previousElem?.focus();
}

const selectPreviousTab = (elem) => {
	const currentIndex = tabsList.indexOf(elem);
	if (currentIndex === -1) throw new Error("No tab index found!");

	if (currentIndex === 0) {
		// We are on the first tab, so select the end tab
		selectTab(tabsList[tabsList.length - 1], true, false);
	} else {
		selectTab(tabsList[currentIndex - 1], true, false);
	}
}

const selectNextTab = (elem) => {
	const currentIndex = tabsList.indexOf(elem);
	if (currentIndex === -1) throw new Error("No tab index found!");

	if (currentIndex < tabsList.length - 1) {
		// We are on the first tab, so select the end tab
		selectTab(tabsList[currentIndex + 1], true, false);
	} else {
		selectTab(tabsList[0], true, false);
	}
}

const selectTab = (elem, shouldSetFocus, shouldOpenTab) => {
	// To ensure we correctly show active tab last, we need to hide the others and then show the new one last
	tabsList.forEach(tab => {
		if (tab == elem) {
			toggleTab({ elem: tab, toShow: shouldOpenTab, shouldSetFocus, tabState: (shouldOpenTab ? 'opening' : 'closing') });
		} else {
			toggleTab({ elem: tab, toShow: false, shouldSetFocus, tabState: 'closing' });
		}
	});

	focusTab(elem);

	if (shouldOpenTab) {
		open();
	}
}

const closeAllTabs = () => {
	tabsList.forEach(tab => {
		toggleTab({ elem: tab, toShow: false, shouldSetFocus: false, tabState: 'closing' });
	});

	close();
}

const focusTab = (elem) => {
	elem?.focus();
}

/**
 * Handles opening or closing of tabs and setting correct active states on other tabs.
 * @param {{
 *   elem: HTMLElement;
 *   toShow: boolean;
 *   shouldSetFocus: boolean;
 *   tabState: string;
 * }}
 */


const toggleTab = ({ elem, toShow, shouldSetFocus, tabState }) => {
	elem.setAttribute('aria-selected', toShow);
	elem.classList.toggle('is-active', toShow);

	if (toShow && shouldSetFocus && tabState === 'focusing') {
		elem.focus();
	}

	// Show the related tab content
	const tabContentId = elem.getAttribute('aria-controls');
	if (!tabContentId) {
		console.warn('No related tab content ID found');
		return;
	}

	const tabContentPanel = document.querySelector(`#${tabContentId}`);
	if (!tabContentPanel) {
		console.warn(`No ${tabContentId} tab content panel found`);
		return;
	}

	if (tabState === 'focusing') {
		return;
	}

	tabContentPanel.classList.toggle('is-active', toShow);

	if (tabState == 'closing') {
		tabContentPanel.querySelectorAll('.is-active').forEach(elem => elem.classList.remove('is-active'));

		// If all tabs are closed we want to remove html active class
		if (areAllElementsClosed()) {
			close();
		}
	}

	tabContentPanelDisplayDefaultContent(tabContentPanel, toShow);
}

// Chek to see are all the elements within the main meganav panels active or not
const areAllElementsClosed = () => {
	return document.querySelectorAll('.meganav .tab-content .is-active').length === 0;
}

const tabContentPanelDisplayDefaultContent = (tabContentPanel, toShow) => {
	// const isAlreadyOpen = tabContentPanel.classList.contains('is-active');
	const shouldShowSummary = tabContentPanel.getAttribute('data-show-panel-summary') === 'true';

	if (shouldShowSummary) {
		const innerTabSummary = tabContentPanel.querySelector('.innerTab__summary');
		if (innerTabSummary) {
			innerTabSummary.classList.toggle('is-active', toShow);
		}
	} else {
		const firstTabListLink = tabContentPanel.querySelector('.meganav__tablist__link');

		if (firstTabListLink) {
			firstTabListLink.classList.toggle('is-active', toShow);
			if (toShow) {
				firstTabListLink.focus();
			}
			const firstTabContent = document.querySelector(`#${firstTabListLink.getAttribute('aria-controls')}`);
			if (firstTabContent) {
				firstTabContent.classList.toggle('is-active', toShow);
			}
		}
	}
}


const tabIntoChildren = (currentTab, isFirstItem) => {
	const tabContentName = currentTab.getAttribute('aria-controls');
	const tabContent = document.querySelector(`#${tabContentName}`);
	if (tabContent == null) {
		console.error("Cannot find tab content");
		return;
	}

	// Grab all elements that could be focused
	const inputs = tabContent.querySelectorAll("select, input, textarea, button, a");
	const firstElement = inputs[0];
	const lastElement = inputs[inputs.length - 1];

	// toggleTab(currentTab, true, true, true);

	selectTab(currentTab, true, true);

	if (isFirstItem) {
		firstElement?.focus();
	} else {
		lastElement?.focus();
	}
}

const initMegaNav = () => {
	if (!hasMegaNavOnPage()) {
		return false;
	}

	setupTabPanels();
}

const setupTabPanels = () => {
	const tabs = Array.from(document.querySelectorAll('.meganav [role=tab]'));
	tabs.forEach(tab => {
		tabsList.push(tab);
		tab.addEventListener('keydown', onKeyDownOnTab);
	});

	// Key listeners
	document.querySelectorAll('nav.meganav').forEach(nav => {
		nav.addEventListener('blur', closeAllTabs);
	});

	document.querySelectorAll('.meganav__tablist__link').forEach(link => {
		link.addEventListener('keydown', onKeyDownOnTabListLink);
	});

	document.querySelectorAll('.meganav__tabs.innerTabs a').forEach(link => {
		link.addEventListener('keydown', onKeyDownOnInnerTabLink);
	});


	// Click listeners
	document.querySelectorAll('.meganav__menu__link').forEach(link => {
		link.addEventListener('click', (e) => {
			const relatedTabId = e.currentTarget.getAttribute('aria-controls');
			if (!relatedTabId) { return false; }

			const relatedTabElem = document.querySelector(`#${relatedTabId}`);
			if (!relatedTabElem) return false;

			document.querySelectorAll('.meganav__menu__link').forEach(link => {
				if (link != e.currentTarget) {
					link.classList.remove('is-active');
				}
			});

			// e.currentTarget.classList.toggle('is-active');
			const isAlreadyOpen = e.currentTarget.classList.contains('is-active');
			selectTab(e.currentTarget, false, !isAlreadyOpen);
		});
	});

	document.querySelectorAll('.meganav__tablist__link').forEach(link => {
		link.addEventListener('click', (e) => {
			setTabListLinkActive(e.currentTarget);
			showInnerTabContent(e.currentTarget);
		});
	});

	// Clicking outside of the mega nav should close it
	document.addEventListener('click', (e) => {
		if (!e.target.closest('.meganav')) {
			closeMegaNavItems();
		}
	});

	// Escape key should close the mega nav
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			closeMegaNavItems();
			focusNextElementAfterMegaNav();
		}
	});

	// loop through each nav__subnav and find the last item that can be tabbled (e..g last subnav-link)
	// If its the last item and we have a tab press, then focus it on the next nav__item
	const tabSubNavs = document.querySelectorAll('.nav__subnav');
	tabSubNavs.forEach(subNav => {
		const inputs = subNav.querySelectorAll("select, input, textarea, button, a");
		const firstElement = inputs[0];
		const lastElement = inputs[inputs.length - 1];

		firstElement?.addEventListener("keydown", (e) => {
			// We need to skip to the previous tab button
			if ((e.key === 'Tab' && e.shiftKey)) {
				e.preventDefault();
				switchToSiblingTab(e.currentTarget, false)
			}
		});

		lastElement?.addEventListener("keydown", (e) => {
			// We need to jump to the next tab button
			if ((e.key === 'Tab' && !e.shiftKey)) {
				e.preventDefault();

				// Check if last tab, if last tab then we need to go to next tabblable element
				const currentTabBtn = getTabButtonOfContentElem(e.currentTarget);
				if (currentTabBtn == null) {
					console.warn('No tab button found!');
					return;
				}

				const currentIndex = tabsList.indexOf(currentTabBtn);
				if (currentIndex == tabsList.length - 1) {
					// We are last tab
					const nextFocusable = getNextFocusableElement(e.currentTarget);
					if (nextFocusable) {
						nextFocusable.focus();

						// Close navigation
						const event = new CustomEvent('PrimaryNavTriggerClose');
						document.dispatchEvent(event);
						closeAllTabs();
					}
				}
				else {
					switchToSiblingTab(e.currentTarget, true)
				}
			}
		});
	});
}

const closeMegaNavItems = () => {
	document.querySelectorAll('.meganav .is-active').forEach(elem => {
		elem.classList.remove('is-active');
	});

	document.querySelectorAll('.meganav [aria-selected=false]').forEach(elem => {
		elem.setAttribute('aria-selected', false)
	});

	close();
}

const getTabButtonOfContentElem = (elem) => {
	const parentGroup = parentsUntil(elem, '.nav__subnav');
	if (parentGroup == null || parentGroup.length == 0) {
		console.warn('No tabparent group found!');
		return null;
	}

	const tabName = parentGroup[0].getAttribute('aria-labelledby');
	const currentTabBtn = document.querySelector(`#${tabName}`);
	if (currentTabBtn == null) {
		console.warn('No tab button found!');
		return null;
	}
	else {
		return currentTabBtn;
	}
}

const switchToSiblingTab = (currentElem, isNext) => {
	const currentTabBtn = getTabButtonOfContentElem(currentElem);
	if (currentTabBtn == null) {
		console.warn('No tab button found!');
		return;
	}

	if (isNext) {
		selectNextTab(currentTabBtn);
	} else {
		currentTabBtn.focus();
	}
}

const showInnerTabContent = (elem) => {
	const relatedContentId = elem.getAttribute('aria-controls');
	const relatedContentElem = document.querySelector(`#${relatedContentId}`);
	if (!relatedContentElem) {
		console.warn('No related content found');
		return;
	}


	hideAllInnerTabs();
	relatedContentElem.classList.add('is-active');

	const focusable = getAllFocusableElementsWithinElement(relatedContentElem);;
	if (focusable) {
		focusable[0].focus();
	}
}

const hideAllInnerTabs = () => {
	const innerTabs = document.querySelectorAll('.innerTab__content');
	innerTabs.forEach(tab => {
		tab.classList.remove('is-active');
	});
}

const hasMegaNavOnPage = () => {
	return document.querySelector('.meganav') !== null;
}

export {
	initMegaNav,
}
